import { createContext, JSX, use, useState } from "react";

import { type Line } from "~/ui/base/Canvas.tsx";
import { PokeSheet } from "~/ui/overlay/PokeSheet.tsx";
import { ShowDrawingSheet } from "~/ui/overlay/ShowDrawingSheet.tsx";
import { ShowGeolocationSheet } from "~/ui/overlay/ShowGeolocationSheet.tsx";
import { ShowPictureSheet } from "~/ui/overlay/ShowPictureSheet.tsx";

export type Layer<Context = unknown> = (props: {
  close: () => void;
  context: Context;
  descriptionId: string;
  titleId: string;
  visible: boolean;
}) => JSX.Element;

export interface OverlaySheets {
  poke: Layer<{ targetUserEmail: string }>;
  showDrawing: Layer<{ lines: Line[]; title: string }>;
  showGeolocation: Layer<{
    position: { latitude: number; longitude: number };
    title: string;
  }>;
  showPicture: Layer<{
    effect: "bounce" | "still";
    title: string;
    videoUrl: string;
  }>;
}

interface Overlay {
  <T>(Component: Layer<T>, context: T): void;
  (Component: Layer): void;
}

const StackedLayerContext = createContext<Overlay>(() => {
  throw new Error(
    "useStackedLayer hook-domain must be called in StackedLayerProvider context",
  );
});

export const SheetsContext = createContext<OverlaySheets>({
  poke: PokeSheet,
  showDrawing: ShowDrawingSheet,
  showGeolocation: ShowGeolocationSheet,
  showPicture: ShowPictureSheet,
});

const isLayer = (layer: Layer | null): layer is Layer => layer !== null;

export const useStackedLayer = () => use(StackedLayerContext);
export const useOverlaySheets = () => use(SheetsContext);

export const StackedLayerProvider = ({
  children,
  unmountAfter,
}: {
  children: JSX.Element;
  unmountAfter: number;
}) => {
  const [visible, setVisible] = useState(false);
  const [context, setContext] = useState<unknown>(null);
  const [Layer, setLayer] = useState<Layer | null>(null);
  const [id, setId] = useState(0);

  const overlay: Overlay = (
    Component: Parameters<Overlay>[0],
    context?: unknown,
  ) => {
    setLayer(() => Component);
    setId((id) => id + 1);
    setVisible(true);
    setContext(context);
  };

  const close = () => {
    setVisible(false);
    setTimeout(() => {
      setVisible((show) => {
        // 딜레이 전에 다른 레이어가 추가되었을 경우, 레이어를 유지한다.
        if (!show) {
          setLayer(null);
        }
        return show;
      });
    }, unmountAfter);
  };

  return (
    <StackedLayerContext.Provider value={overlay}>
      {children}
      {isLayer(Layer) && (
        <Layer
          close={close}
          context={context}
          descriptionId={String(id) + ":description"}
          titleId={String(id) + ":title"}
          visible={visible}
        />
      )}
    </StackedLayerContext.Provider>
  );
};
